import * as React from "react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import { Button, Dialog } from "@/components/ui";
import { SocoAiLogo } from "@/components/Holo";

const STEPS = [
  {
    title: "1. Describe it",
    body: "Tell Prompt Studio the look you want, or start from a template. It designs the layout, backgrounds, and type.",
  },
  {
    title: "2. Refine and approve",
    body: "Ask for changes in plain language, compare before and after, and keep the version you like.",
  },
  {
    title: "3. Publish",
    body: "Publishing adds the design to your prompt library. Choose who receives it, just like any prompt.",
  },
  {
    title: "4. Players share",
    body: "When a player shares a review, their post is built from your design with their name, headshot, and branch branding.",
  },
];

const FAQ = [
  {
    q: "Can I change a design after publishing?",
    a: "Yes. Refine it and keep the new version. Players see the update on their next post. Posts already made don't change.",
  },
  {
    q: "Who can use Prompt Studio?",
    a: "Coaches and assistants with the Prompt Studio permission. Review designs also need Reputation Manager.",
  },
  {
    q: "How long does it take?",
    a: "Usually about a minute to generate and under a minute to refine. You can leave and come back.",
  },
  {
    q: "Can two people work on the same design?",
    a: "Yes. You'll see who's viewing, and changes appear for everyone as they happen.",
  },
];

export interface HelpDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
}

export const PromptStudioHelpDialog: React.FC<HelpDialogProps> = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="xl">
    <Dialog.Header>
      <Dialog.Title className="flex items-center gap-2 text-xl font-bold font-sans">
        <SocoAiLogo variant="mono" height={16} />
        <span>Prompt Studio</span>
      </Dialog.Title>
    </Dialog.Header>

    <Dialog.Content className="max-h-[70vh] overflow-y-auto space-y-6 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {STEPS.map(step => (
          <div
            key={step.title}
            className="bg-slate-50 border border-slate-200/50 p-4 rounded-xl leading-normal text-sm text-slate-600 space-y-2"
          >
            <div className="font-semibold text-slate-800 text-base">{step.title}</div>
            <p>{step.body}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-normal">
        <AlertCircle className="size-5 shrink-0 text-blue-500 mt-0.5" aria-hidden="true" />
        <p>
          Design it once with AI, then it's locked in. Every post your players share is built from exactly the design
          you approved. Their name, headshot, and branding drop into your layout, and nothing gets redrawn or
          reinvented, so every post looks as professional as the one you signed off on.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 text-sm leading-normal text-slate-600">
        {FAQ.map(item => (
          <div key={item.q} className="space-y-1">
            <span className="text-base font-bold text-slate-800 flex items-center gap-1">
              <ShieldCheck className="size-4 text-emerald-500 shrink-0" aria-hidden="true" />
              {item.q}
            </span>
            <p>{item.a}</p>
          </div>
        ))}
      </div>
    </Dialog.Content>

    <Dialog.Footer>
      <Button onClick={() => onOpenChange(false)}>Close Help</Button>
    </Dialog.Footer>
  </Dialog>
);
